/**
 * PriceSetLineItemRow — single inline-editable row in a price set.
 * Supports drag-to-reorder via @dnd-kit, and inline field editing with
 * Tab-to-next-field and auto-save on blur (with 500ms debounce).
 */
import {
    Box,
    HStack,
    Input,
    Select,
    IconButton,
    Text,
    Tooltip,
    useToast,
    useColorModeValue,
} from '@chakra-ui/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useRef, useState } from 'react';

import { MdDragIndicator, MdDelete } from 'react-icons/md';
import { useUpdateLineItem, useDeleteLineItem } from '@/services/hooks';

const UOM_OPTIONS = [
    'EA', 'LS', 'LF', 'SF', 'SY', 'CY', 'TN', 'HR', 'DAY', 'MO',
    'GAL', 'LB', 'CF', 'MSF', 'MGAL', 'IN', 'FT', 'YD',
];

const CATEGORY_OPTIONS = ['Materials', 'Labor', 'Equipment', 'Subcontractor', 'Other'];


interface LineItem {
    id: number;
    name: string;
    description?: string;
    category?: string;
    unit_price: number;
    uom: string;
    notes?: string;
}

interface Props {
    item: LineItem;
    setId: number;
    isNew?: boolean;
    onFocusNext?: () => void;
}

export function PriceSetLineItemRow({ item, setId, onFocusNext }: Props) {
    const toast = useToast();
    const updateMutation = useUpdateLineItem();
    const deleteMutation = useDeleteLineItem();

    // Local state mirrors the item for optimistic editing
    const [fields, setFields] = useState({
        name: item.name,
        category: item.category || '',
        unit_price: String(item.unit_price),
        uom: item.uom,
    });

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // dnd-kit sortable
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item.id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        zIndex: isDragging ? 999 : 'auto',
    };

    const save = (updated: typeof fields) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            const priceVal = parseFloat(updated.unit_price);
            if (isNaN(priceVal) || !updated.name.trim()) return;
            try {
                await updateMutation.mutateAsync({
                    setId,
                    itemId: item.id,
                    data: {
                        name: updated.name.trim(),
                        category: updated.category || null,
                        unit_price: priceVal,
                        uom: updated.uom,
                    },
                });
            } catch {
                toast({ title: 'Failed to save', status: 'error', duration: 2000, isClosable: true });
            }
        }, 500);
    };

    const handleChange = (key: keyof typeof fields, value: string) => {
        const next = { ...fields, [key]: value };
        setFields(next);
        save(next);
    };

    const handleDelete = async () => {
        try {
            await deleteMutation.mutateAsync({ setId, itemId: item.id });
        } catch {
            toast({ title: 'Failed to delete', status: 'error', duration: 2000, isClosable: true });
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Tab' && !e.shiftKey) {
            // natural tab; let browser handle, but trigger next row focus if at last field
        }
        if (e.key === 'Enter') {
            onFocusNext?.();
        }
    };

    const rowBg = useColorModeValue('white', 'navy.700');
    const rowHoverBg = useColorModeValue('gray.50', 'navy.650');
    const dragIconColor = useColorModeValue('gray.400', 'gray.600');
    const dragIconHoverColor = useColorModeValue('gray.600', 'gray.400');
    const inputColor = useColorModeValue('gray.800', 'gray.100');
    const placeholderColor = useColorModeValue('gray.400', 'gray.600');
    const selectColor = useColorModeValue('gray.800', 'gray.300');
    const selectPlaceholderColor = useColorModeValue('gray.400', 'gray.600');
    const selectOptionBg = useColorModeValue('white', 'navy.700');
    const selectOptionColor = useColorModeValue('gray.800', 'gray.100');
    const currencySymbolColor = useColorModeValue('gray.500', 'gray.500');
    const priceColor = useColorModeValue('green.600', 'green.300');
    const rowBorder = useColorModeValue('1px solid', '1px solid');
    const rowBorderColor = useColorModeValue('gray.200', 'transparent');

    return (
        <Box
            ref={setNodeRef}
            style={style as any}
            px={2}
            py={1.5}
            borderRadius="md"
            bg={rowBg}
            border={rowBorder}
            borderColor={rowBorderColor}
            _hover={{ bg: rowHoverBg }}
            transition="background 0.15s"
            role="group"
            boxShadow={useColorModeValue('sm', 'none')}
        >
            <HStack spacing={2} align="center">
                {/* Drag handle */}
                <Box
                    {...attributes}
                    {...listeners}
                    cursor="grab"
                    color={dragIconColor}
                    _hover={{ color: dragIconHoverColor }}
                    flexShrink={0}
                    pl={0.5}
                >
                    <MdDragIndicator />
                </Box>

                {/* Name */}
                <Input
                    size="sm"
                    variant="unstyled"
                    placeholder="Item name"
                    value={fields.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    onKeyDown={handleKeyDown}
                    fontWeight="500"
                    color={inputColor}
                    flex={3}
                    _placeholder={{ color: placeholderColor }}
                    _focus={{ outline: 'none', borderBottom: '1px solid', borderColor: 'brand.500' }}
                    minW="120px"
                />

                {/* Category */}
                <Select
                    size="sm"
                    variant="unstyled"
                    value={fields.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    color={fields.category ? selectColor : selectPlaceholderColor}
                    flex={1.5}
                    minW="90px"
                    fontSize="xs"
                    sx={{
                        option: { bg: selectOptionBg, color: selectOptionColor },
                    }}
                >
                    <option value="">Category</option>
                    {CATEGORY_OPTIONS.map((c) => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </Select>

                {/* UOM */}
                <Select
                    size="sm"
                    variant="unstyled"
                    value={fields.uom}
                    onChange={(e) => handleChange('uom', e.target.value)}
                    color={selectColor}
                    flex={1}
                    minW="60px"
                    fontSize="xs"
                    sx={{
                        option: { bg: selectOptionBg, color: selectOptionColor },
                    }}
                >
                    {UOM_OPTIONS.map((u) => (
                        <option key={u} value={u}>{u}</option>
                    ))}
                </Select>

                {/* Unit price */}
                <Box flex={1.5} minW="90px" position="relative">
                    <Text
                        position="absolute"
                        left={1.5}
                        top="50%"
                        transform="translateY(-50%)"
                        fontSize="xs"
                        color={currencySymbolColor}
                        pointerEvents="none"
                        zIndex={1}
                    >
                        $
                    </Text>
                    <Input
                        size="sm"
                        variant="unstyled"
                        type="number"
                        min={0}
                        step="0.01"
                        value={fields.unit_price}
                        onChange={(e) => handleChange('unit_price', e.target.value)}
                        onKeyDown={handleKeyDown}
                        pl={4}
                        fontWeight="600"
                        color={priceColor}
                        textAlign="right"
                        _placeholder={{ color: placeholderColor }}
                        _focus={{ outline: 'none', borderBottom: '1px solid', borderColor: 'brand.500' }}
                    />
                </Box>

                {/* Delete */}
                <Tooltip label="Remove" hasArrow>
                    <IconButton
                        aria-label="Delete line item"
                        icon={<MdDelete />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        opacity={0}
                        _groupHover={{ opacity: 1 }}
                        onClick={handleDelete}
                        isLoading={deleteMutation.isPending}
                        flexShrink={0}
                    />
                </Tooltip>
            </HStack>
        </Box>
    );
}
